import * as React from 'react';
import { connect } from 'react-redux';
import { sortBy } from 'lodash';
import type { FormikErrors, FormikTouched} from 'formik';
import { Field } from 'formik';
import { FieldRow } from '../../field-row/FieldRow';
import { FieldValidation } from '../../field-validation/FieldValidation';
import type { PupilValues } from '../CustomerDetailsForm';
import { PupilFormType } from '../../../containers/pupil-details/PupilDetails';
import type { RootState } from '../../../app/store';

interface InjectedProps {
    counties: endpoints.County[];
}

interface OwnProps {
    formType?: PupilFormType;
    values: PupilValues;
    errors: FormikErrors<PupilValues>;
    touched: FormikTouched<PupilValues>;
    // tslint:disable-next-line:no-any
    setFieldRef?: (field: string, node: any) => void;
}

export type Props = OwnProps & InjectedProps;

export class PupilFields extends React.PureComponent<Props> {
    render() {
        const { touched, errors, counties, setFieldRef, formType } = this.props;
        const dobError = errors.dob as typeof errors.dob | string;
        return (
            <div data-testid="pupil-fields">
                <h3>
                    Pupil Details
                </h3>
                {formType === PupilFormType.ThirdParty &&
                    <div data-testid="marketing-consent" className="pupilDetails__message"><span><strong>
                        * Please make sure that the person you are purchasing the lessons
                        for has given you their consent to provide their personal information to us.
                    </strong></span></div>
                }
                <hr className="a-keyline-divider--horizontal-solid-light" />
                <FieldRow
                    touched={touched.firstName}
                    error={!!errors.firstName}
                    ref={node => setFieldRef && setFieldRef('pupil.firstName', node)}
                >
                    <label className="a-label" htmlFor="firstName">
                        First Name
                        </label>
                    <FieldValidation
                        touched={touched.firstName}
                        errMessage={errors.firstName}
                    />
                    <Field
                        type="text"
                        name="pupil.firstName"
                        className="a-textbox"
                        placeholder="Enter first name here"
                        id="firstName"
                    />
                </FieldRow>
                <FieldRow
                    touched={touched.surname}
                    error={!!errors.surname}
                    ref={node => setFieldRef && setFieldRef('pupil.surname', node)}
                >
                    <label className="a-label" htmlFor="surname">Surname</label>
                    <FieldValidation
                        touched={touched.surname}
                        errMessage={errors.surname}
                    />
                    <Field
                        type="text"
                        name="pupil.surname"
                        className="a-textbox"
                        placeholder="Enter surname here"
                        id="surname"
                    />
                </FieldRow>
                <FieldRow
                    touched={touched.dob
                        && touched.dob.dobDay
                        && touched.dob.dobMonth
                        && touched.dob.dobYear
                    }
                    error={!!errors.dob}
                    ref={node => setFieldRef && setFieldRef('pupil.dob', node)}
                >
                    <label className="a-label" htmlFor='dobDay'>Date of birth</label>
                    <FieldValidation
                        touched={touched.dob
                            && touched.dob.dobDay
                            && touched.dob.dobMonth
                            && touched.dob.dobYear
                        }
                        errMessage={typeof dobError === 'string'
                            ? dobError
                            : dobError?.dobDay || dobError?.dobMonth || dobError?.dobYear}
                    />
                    <ul className="m-textbox-group">
                        <li className="m-textbox-group__item">
                            <Field
                                type="tel"
                                name="pupil.dob.dobDay"
                                placeholder="DD"
                                minLength="1"
                                maxLength="2"
                                className="a-textbox a-textbox--2-character"
                                id="dobDay"
                            />
                        </li>
                        <li className="m-textbox-group__item">
                            <Field
                                name="pupil.dob.dobMonth"
                                type="tel"
                                minLength="1"
                                maxLength="2"
                                placeholder="MM"
                                className="a-textbox a-textbox--2-character"
                            />
                        </li>
                        <li className="m-textbox-group__item">
                            <Field
                                name="pupil.dob.dobYear"
                                type="tel"
                                placeholder="YYYY"
                                minLength="4"
                                maxLength="4"
                                className="a-textbox a-textbox--4-character"
                            />
                        </li>
                    </ul >
                </FieldRow>
                <div className="address-fields">
                    <FieldRow
                        touched={touched.addressLine1}
                        error={!!errors.addressLine1}
                        ref={node => setFieldRef && setFieldRef('pupil.addressLine1', node)}
                        className="m-form-row--field-group"
                    >
                        <label className="a-label" htmlFor="addressLine1">Address</label>
                        <FieldValidation
                            touched={touched.addressLine1}
                            errMessage={errors.addressLine1}
                        />
                        <Field
                            name="pupil.addressLine1"
                            className="a-textbox"
                            placeholder="House name / number"
                            id="addressLine1"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.addressLine2}
                        error={!!errors.addressLine2}
                        ref={node => setFieldRef && setFieldRef('pupil.addressLine2', node)}
                        className="m-form-row--field-group"
                    >
                        <FieldValidation
                            touched={touched.addressLine2}
                            errMessage={errors.addressLine2}
                        />
                        <Field
                            name="pupil.addressLine2"
                            className="a-textbox"
                            placeholder="Street name"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.town}
                        error={!!errors.town}
                        ref={node => setFieldRef && setFieldRef('pupil.town', node)}
                        className="m-form-row--field-group"
                    >
                        <FieldValidation
                            touched={touched.town}
                            errMessage={errors.town}
                        />
                        <Field
                            name="pupil.town"
                            className="a-textbox"
                            placeholder="Town"
                        />
                    </FieldRow>
                    <FieldRow
                        touched={touched.countyId}
                        error={!!errors.countyId}
                        ref={node => setFieldRef && setFieldRef('pupil.countyId', node)}
                        className="m-form-row--field-group"
                    >
                        <FieldValidation
                            touched={touched.countyId}
                            errMessage={errors.countyId}
                        />
                        <span className="a-dropdown">
                            <Field
                                component="select"
                                name="pupil.countyId"
                                aria-label="Select county"
                                className="a-dropdown__select"
                            >
                                <option value={undefined}>County</option>
                                {sortBy(counties, county => county.name)
                                    .map(county => (
                                        <option value={county.countyId} key={county.countyId}>
                                            {county.name}
                                        </option>
                                    ))}
                            </Field>
                            <span className="a-dropdown__ui" />
                        </span>
                    </FieldRow>
                </div>
                <h3>Pupil Contact Details</h3>
                <hr className="a-keyline-divider--horizontal-solid-light" />
                <FieldRow
                    touched={touched.mobile}
                    error={!!errors.mobile}
                    ref={node => setFieldRef && setFieldRef('pupil.mobile', node)}
                >
                    <label className="a-label" htmlFor="mobile">Mobile phone number</label>
                    <FieldValidation
                        touched={touched.mobile}
                        errMessage={errors.mobile}
                    />
                    <Field
                        name="pupil.mobile"
                        type="tel"
                        className="a-textbox"
                        placeholder="Enter your mobile phone number here"
                        id="mobile"
                    />
                </FieldRow>
                <FieldRow
                    touched={touched.emergencyNumber}
                    error={!!errors.emergencyNumber}
                    ref={node => setFieldRef && setFieldRef('pupil.emergencyNumber', node)}
                >
                    <label className="a-label" htmlFor="emergencyNumber">Emergency telephone number</label>
                    <FieldValidation
                        touched={touched.emergencyNumber}
                        errMessage={errors.emergencyNumber}
                    />
                    <Field
                        name="pupil.emergencyNumber"
                        type="tel"
                        className="a-textbox"
                        placeholder="Enter contact number for use in case of emergency"
                        id="emergencyNumber"
                    />
                </FieldRow>
                <FieldRow
                    touched={touched.email}
                    error={!!errors.email}
                    ref={node => setFieldRef && setFieldRef('pupil.email', node)}
                >
                    <label className="a-label" htmlFor="email">Email address</label>
                    <p>We'll send your booking confirmation to this email address.</p>
                    <FieldValidation
                        touched={touched.email}
                        errMessage={errors.email}
                    />
                    <Field
                        name="pupil.email"
                        type="email"
                        className="a-textbox"
                        placeholder="Enter email address"
                        id="email"
                    />
                </FieldRow>
                <FieldRow
                    touched={touched.confirmEmail}
                    error={!!errors.confirmEmail}
                    ref={node => setFieldRef && setFieldRef('pupil.confirmEmail', node)}
                >
                    <label className="a-label" htmlFor="confirmEmail">Reconfirm email address</label>
                    <FieldValidation
                        touched={touched.confirmEmail}
                        errMessage={errors.confirmEmail}
                    />
                    <Field
                        name="pupil.confirmEmail"
                        type="email"
                        className="a-textbox"
                        placeholder="Enter email address"
                        id="confirmEmail"
                    />
                </FieldRow>
            </div>
        );
    }
}

export function mapStateToProps(state: RootState): InjectedProps {
    return {
        counties: state.counties.countiesAvailable
    };
}

export default connect<InjectedProps, {}, OwnProps, RootState>(mapStateToProps)(PupilFields);
